import { useEffect, useState, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { getActiveCallsNamespace, getBackendOrigin, getSocketIoPath } from '../lib/socket';

export interface ActiveCall {
  call_id: string;
  caller_phone: string;
  caller_id: number;
  status: string;
  detected_user_name: string;
  conversation_state: string;
  start_time: Date | string;
  current_turn?: number;
  total_messages?: number;
}

interface UseActiveCallsReturn {
  activeCalls: ActiveCall[];
  isConnected: boolean;
  totalActive: number;
  refreshActiveCalls: () => void;
}

const backendOrigin = getBackendOrigin();
const socketPath = getSocketIoPath();
const activeCallsNamespace = getActiveCallsNamespace();

export function useActiveCalls(autoConnect = true): UseActiveCallsReturn {
  const [activeCalls, setActiveCalls] = useState<ActiveCall[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!autoConnect) return;

    // Initialize socket connection
    const socketUrl = `${backendOrigin}${activeCallsNamespace}`;
    const token =
      typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem('access_token')
        : null;

    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      path: socketPath,
      auth: token && token !== 'undefined' ? { token } : undefined,
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      console.log('✅ Connected to WebSocket Server (Active Calls)', {
        socketUrl,
        path: socketPath,
        id: socket.id,
      });
      setIsConnected(true);
      
      // Join a monitoring/admin room to receive all call events
      socket.emit('join', { room: 'admin_monitoring' });
      socket.emit('join', { room: 'call_monitoring' });
      
      // Request initial active calls list
      socket.emit('get_active_calls');
    });

    socket.on('connect_error', (error) => {
      const err = error as unknown as { message?: string; description?: string; context?: unknown; data?: unknown };
      console.error('❌ Connection Error (Active Calls):', {
        message: err?.message,
        description: err?.description,
        data: err?.data,
        context: err?.context,
        socketUrl,
        path: socketPath,
        namespace: activeCallsNamespace || '/',
        hasToken: Boolean(token && token !== 'undefined'),
      });
      setIsConnected(false);
    });

    socket.on('disconnect', () => {
      console.log('🔌 Disconnected from WebSocket Server (Active Calls)');
      setIsConnected(false);
      // Clear all active calls on disconnect
      setActiveCalls([]);
    });

    // Listen for active calls list updates
    socket.on('active_calls_update', (data: { active_calls: ActiveCall[]; total_active: number }) => {
    //   console.log('📋 Active calls update:', data);
      setActiveCalls(data.active_calls || []);
    });

    // Listen for new call started
    socket.on('call_started', (data: { call_id: string; from_number?: string; caller_id?: number; detected_user_name?: string }) => {
      
      // Join the specific call room to receive call_ended events
      if (data.call_id) {
        socket.emit('join', { room: `call_${data.call_id}` });
        console.log(`Joined room: call_${data.call_id}`);
      }
      
      // Add new call to the list
      const newCall: ActiveCall = {
        call_id: data.call_id,
        caller_phone: data.from_number || 'Unknown',
        caller_id: data.caller_id || 0,
        status: 'connected',
        detected_user_name: data.detected_user_name || 'Unknown Patient',
        conversation_state: 'greeting',
        start_time: new Date(),
        current_turn: 0,
        total_messages: 0,
      };

      setActiveCalls(prev => {
        // Check if call already exists
        const exists = prev.some(call => call.call_id === data.call_id);
        if (exists) return prev;
        return [...prev, newCall];
      });
    });

    // Listen for call ended - using multiple possible event names
    const handleCallEnded = (data: unknown) => {
      console.log('🔴 Call ended event received:', data);
      console.log('🔴 Event data type:', typeof data);
      console.log('🔴 Event data keys:', data && typeof data === 'object' ? Object.keys(data) : 'not an object');
      
      const dataObj = data as Record<string, unknown>;
      const callId = dataObj?.call_id || dataObj?.callId || dataObj?.id;
      
      if (!callId || typeof callId !== 'string') {
        console.warn('call_ended event received without valid call_id. Data:', data);
        return;
      }
      
      // Remove call from active list
      setActiveCalls(prev => {
        console.log(`🔴 Before removal, active calls:`, prev.map(c => c.call_id));
        const updated = prev.filter(call => call.call_id !== callId);
        console.log(`🔴 After removal of ${callId}, active calls:`, updated.map(c => c.call_id));
        console.log(`🔴 Removed call ${callId}. Active calls count: ${updated.length}`);
        return updated;
      });
    };

    socket.on('call_ended', handleCallEnded);
    socket.on('callEnded', handleCallEnded); // Try alternative event name
    socket.on('call_end', handleCallEnded);   // Try alternative event name

    // Listen for call status updates
    socket.on('call_status_update', (data: { call_id: string; status: string; conversation_state?: string }) => {
      console.log('🔄 Call status update:', data);
      
      setActiveCalls(prev => prev.map(call => 
        call.call_id === data.call_id 
          ? { 
              ...call, 
              status: data.status,
              conversation_state: data.conversation_state || call.conversation_state 
            }
          : call
      ));
    });

    // Request active calls periodically as fallback
    const interval = setInterval(() => {
      if (socket.connected) {
        console.log('🔄 Requesting active calls (periodic check)');
        socket.emit('get_active_calls');
      }
    }, 10000); // Every 10 seconds

    // Also request immediately after any call event
    socket.on('call_started', () => {
      setTimeout(() => socket.emit('get_active_calls'), 500);
    });
    
    socket.on('call_ended', () => {
      setTimeout(() => socket.emit('get_active_calls'), 500);
    });

    socket.on('callEnded', () => {
      setTimeout(() => socket.emit('get_active_calls'), 500);
    });

    socket.on('call_end', () => {
      setTimeout(() => socket.emit('get_active_calls'), 500);
    });

    // Cleanup on unmount
    return () => {
      clearInterval(interval);
      socket.disconnect();
    };
  }, [autoConnect]);

  const refreshActiveCalls = useCallback(() => {
    if (socketRef.current?.connected) {
      console.log('🔄 Manual refresh of active calls');
      socketRef.current.emit('get_active_calls');
    }
  }, []);

  return {
    activeCalls,
    isConnected,
    totalActive: activeCalls.length,
    refreshActiveCalls,
  };
}
